import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useWebRTCContext } from './WebRTCContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Mic, MicOff, PhoneOff, UserPlus, Users, X, Volume2 } from 'lucide-react';

// Per-participant hidden audio sink. Attaches the remote stream and reports real media
// readiness back to the conference (so a peer is only "Connected" once audio flows).
// If the browser blocks autoplay, we surface a "Tap to enable audio" button (user gesture).
const RemoteAudio = ({ connId, stream, reportMedia }) => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || !stream) return;
    if (el.srcObject !== stream) el.srcObject = stream;
    el.play()
      .then(() => { setBlocked(false); reportMedia && reportMedia(connId, 'ready'); })
      .catch(() => { setBlocked(true); reportMedia && reportMedia(connId, 'no-audio'); });
  }, [stream, connId, reportMedia]);

  const enable = () => {
    const el = ref.current;
    if (!el) return;
    el.play().then(() => { setBlocked(false); reportMedia && reportMedia(connId, 'ready'); }).catch(() => {});
  };

  return (
    <>
      <audio ref={ref} autoPlay playsInline />
      {blocked && (
        <button onClick={enable} className="text-xs text-amber-600 underline flex items-center gap-1">
          <Volume2 className="h-3 w-3" /> {t('st.conf.tapEnableAudio')}
        </button>
      )}
    </>
  );
};

// Renders the staff AUDIO conference UI: the incoming-invite modal and the active
// conference panel (participants + per-participant audio sink + mute + add/remove member +
// leave/end). Consumes the conference API exposed on WebRTCContext by WebRTCProvider.
const ConferenceUI = () => {
  const { t } = useLanguage();
  const rtc = useWebRTCContext();
  const {
    conference, participants, incomingInvite, selfMuted, confError, isConfHost,
    inviteMember, removeMember, reportMedia, acceptInvite, rejectInvite, toggleConfMute, leaveConference,
    onlineUsers, resolveStaff, baseUid,
  } = rtc;

  // Online staff not already in the conference (for "Add member" / re-add).
  const addable = useMemo(() => {
    const inConf = new Set((participants || [])
      .filter((p) => !['declined', 'timedout'].includes(p.state)) // declined/no-answer can be re-added
      .map((p) => (p.connId || '').split('__')[0]));
    return (onlineUsers || [])
      .filter((u) => u && u !== baseUid && !inConf.has(u))
      .map((u) => ({ uid: u, ...(resolveStaff && resolveStaff(u) ? resolveStaff(u) : { name: 'Staff member', role: '' }) }));
  }, [onlineUsers, participants, baseUid, resolveStaff]);

  // Label combines signaling/pc state with real media readiness.
  const stateLabel = (p) => {
    const s = p.state;
    if (s === 'invited') return { t: t('st.conf.inviting'), c: 'text-amber-600' };
    if (s === 'timedout') return { t: t('st.conf.noAnswer'), c: 'text-gray-400' };
    if (s === 'declined') return { t: t('st.conf.declined'), c: 'text-red-500' };
    if (s === 'reconnecting') return { t: t('st.conf.reconnecting'), c: 'text-amber-600' };
    if (s === 'failed' || s === 'closed') return { t: t('st.conf.lost'), c: 'text-red-500' };
    if (s === 'connected') {
      if (p.media === 'ready') return { t: t('st.conf.connected'), c: 'text-green-600' };
      if (p.media === 'no-audio') return { t: t('st.conf.noAudio'), c: 'text-amber-600' };
      return { t: t('st.conf.connectingAudio'), c: 'text-gray-500' };
    }
    return { t: t('st.conf.connecting'), c: 'text-gray-500' };
  };

  return (
    <>
      {/* Incoming conference invite */}
      {incomingInvite && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-[340px] text-center">
            <div className="mx-auto mb-3 h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center">
              <Users className="h-6 w-6 text-orange-600" />
            </div>
            <div className="text-lg font-semibold">{t('st.conf.audioConference')}</div>
            <div className="text-sm text-gray-600 mt-1">{t('st.conf.invitingYou', { host: incomingInvite.hostName })}</div>
            <div className="flex gap-3 mt-5">
              <button onClick={rejectInvite} className="flex-1 py-2 rounded-lg bg-gray-100 text-gray-700 font-medium">{t('st.conf.decline')}</button>
              <button onClick={acceptInvite} className="flex-1 py-2 rounded-lg bg-green-600 text-white font-medium">{t('st.conf.accept')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Active conference panel */}
      {conference && (
        <div className="fixed bottom-4 right-4 z-[65] w-[320px] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-orange-600 text-white flex items-center justify-between">
            <div className="flex items-center gap-2 font-semibold"><Users className="h-4 w-4" /> {t('st.conf.audioConference')}</div>
            <span className="text-xs opacity-90">{isConfHost ? t('st.conf.host') : t('st.conf.participant')}</span>
          </div>

          <div className="max-h-[260px] overflow-auto px-4 py-3 space-y-2">
            {/* Self */}
            <div className="flex items-center justify-between">
              <div className="text-sm">
                <span className="font-medium">{t('st.conf.you')}</span>
                <span className="text-xs text-gray-500 ml-1">{selfMuted ? t('st.conf.muted') : ''}</span>
              </div>
              <button
                onClick={toggleConfMute}
                title={selfMuted ? t('st.conf.unmute') : t('st.conf.mute')}
                className={`h-8 w-8 rounded-full flex items-center justify-center ${selfMuted ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-700'}`}
              >
                {selfMuted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              </button>
            </div>

            {/* Remote participants */}
            {(participants || []).length === 0 && (
              <div className="text-xs text-gray-500">{t('st.conf.noOneYet')}</div>
            )}
            {(participants || []).map((p) => {
              const sl = stateLabel(p);
              return (
                <div key={p.connId} className="flex items-center justify-between gap-2">
                  <div className="text-sm min-w-0">
                    <span className="font-medium">{p.name || 'Staff member'}</span>
                    {p.role ? <span className="text-xs text-gray-400 ml-1">{p.role}</span> : null}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-xs ${sl.c}`}>{sl.t}</span>
                    {/* hidden audio sink + autoplay affordance */}
                    {p.stream && <RemoteAudio connId={p.connId} stream={p.stream} reportMedia={reportMedia} />}
                    {/* host: remove participant */}
                    {isConfHost && (
                      <button
                        onClick={() => removeMember(p.connId)}
                        title={t('st.conf.removeFromConf')}
                        className="h-6 w-6 rounded-full flex items-center justify-center bg-gray-100 text-gray-500 hover:bg-red-100 hover:text-red-600"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Add member / re-add (host only) */}
          {isConfHost && (
            <div className="px-4 py-2 border-t border-gray-100">
              {addable.length > 0 ? (
                <select
                  value=""
                  onChange={(e) => { if (e.target.value) { inviteMember(e.target.value); } }}
                  className="w-full text-sm border border-gray-200 rounded-lg px-2 py-2 text-gray-700"
                >
                  <option value="" disabled>{t('st.conf.addTeammate')}</option>
                  {addable.map((a) => <option key={a.uid} value={a.uid}>{a.name}{a.role ? ` · ${a.role}` : ''}</option>)}
                </select>
              ) : (
                <div className="text-xs text-gray-400 flex items-center gap-1"><UserPlus className="h-3 w-3" /> {t('st.conf.noStaffOnline')}</div>
              )}
            </div>
          )}

          {confError && <div className="px-4 py-1 text-xs text-red-500">{confError}</div>}

          <div className="px-4 py-3 border-t border-gray-100">
            <button onClick={leaveConference} className="w-full py-2 rounded-lg bg-red-600 text-white font-medium flex items-center justify-center gap-2">
              <PhoneOff className="h-4 w-4" /> {isConfHost ? t('st.conf.endConference') : t('st.conf.leave')}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ConferenceUI;
